import * as React from 'react';
import { chunk, isEmpty } from 'lodash';
import { translate } from 'components/translate-hoc';
// components
import Logos from './subcomponents/logos.component';
import LegalsLinks from './subcomponents/legalsLinks.component';
import FooterLanguageSelect from './footer-language-select';
import AgeRatings from 'modules/age-ratings';
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';
// constants
import {
    ANALYTICS_ACTION,
    FOOTER_LOGO_CLASSNAMES,
    TEXT_KEYS,
    LEGALS_LINKS_ANALYTICS_ATTRIBUTES
} from './footer.constants';
// structure
import { linksList, logosList, platformLogosList, regionLinksList } from './footer.structure';
// utils
import { insert, remove } from 'utils/array';
import { getCountryRegion } from './footer.utils';
// types
import { PLATFORM_TYPES } from '@sitegen/common/constants/platforms';
import { IFooterLink, IFooterProps, IFooterState } from './footer.types';
// styles
import './footer.styles.less';

export class Footer extends React.Component<IFooterProps, IFooterState> {
    public currentYear: number = new Date().getUTCFullYear();

    public handleLanguageChange = async (
        event: React.ChangeEvent<HTMLSelectElement>
    ): Promise<void> => this.props.fetchLanguage(event.target.value);

    public getLegalsLinks = () => {
        const { countryCode } = this.props;
        const regionLinks = regionLinksList[getCountryRegion(countryCode)] || [];
        const links = [
            ...(this.props.linksList ? this.props.linksList : linksList),
            ...regionLinks
        ];

        return !isEmpty(links) ? this.generateLegalsLinks(links) : [];
    };

    public generateLegalsLinks = (links: IFooterLink[]) => {
        const { dangerouslyTranslateWithHTML: translateKey } = this.props;
        const rows = chunk(links, 2);
        const createClickHandler = (link: IFooterLink): React.MouseEventHandler => {
            return link.analyticsAction === ANALYTICS_ACTION.INIT_PRIVACY_OPTIONS ?
                (event: React.MouseEvent<HTMLAnchorElement>) => {
                    event.preventDefault();
                    AnalyticsService.initPrivacyOptions();
                } : () => AnalyticsService.sendFlowEvent({
                    ...LEGALS_LINKS_ANALYTICS_ATTRIBUTES,
                    action: link.analyticsAction.toLowerCase()
                });
        };

        return rows.map((row, rowIndex) => (
            <div className="sitegen-footer__nav-row" key={rowIndex}>
                {row.map((link: IFooterLink, linkIndex) => (
                    <div
                        key={linkIndex}
                        className={`sitegen-footer__nav-item ${
                            this.isLastElement({
                                rowAmount: rows.length,
                                linkAmount: row.length,
                                rowIndex,
                                linkIndex
                            }) ? 'last' : ''
                        }`}
                    >
                        <a
                            className="sitegen-footer__nav-link"                            
                            href={link.href}
                            rel="noreferrer"
                            target="_blank"
                            onClick={createClickHandler(link)}
                        >
                            {translateKey(link.key)}
                        </a>
                    </div>
                ))}
            </div>
        ));
    };

    public isLastElement = ({ rowAmount, linkAmount, rowIndex, linkIndex }: {
        [_key: string]: number
    }) => rowAmount === (rowIndex + 1) && linkAmount === (linkIndex + 1);

    public getLogos = () => {
        const { availablePlatforms } = this.props;
        const platformLogos = platformLogosList.filter(
            platform => availablePlatforms.includes(platform.platformId)
        );

        // should render epic before connect
        // https://mdc-tomcat-jira35.ubisoft.org/jira/browse/GEN-2206
        const epicLogoPosition = platformLogos.findIndex(
            (platform) => platform.platformId === PLATFORM_TYPES.EPIC
        );
        const connectLogoPosition = logosList.findIndex(
            (logo) => logo.className === FOOTER_LOGO_CLASSNAMES.CONNECT
        );

        return epicLogoPosition === -1
            ? [...logosList, ...platformLogos]
            : [
                ...insert(logosList, connectLogoPosition, platformLogos[epicLogoPosition]),
                ...remove(platformLogos, epicLogoPosition)
            ];
    };

    public render() {
        const { dangerouslyTranslateWithHTML: translateKey, ratingsConfig } = this.props;
        const logos = this.getLogos();
        const legalsLinks = this.getLegalsLinks();
        const copyrightText: string = translateKey(
            TEXT_KEYS.FOOTER_COPYRIGHT, { footerCopyrightYear: this.currentYear }
        );

        return (
            <div className="sitegen-footer">
                <div className="sitegen-footer__content">
                    <div className="sitegen-footer__language-select-column">
                        <FooterLanguageSelect
                            activeLanguageTag={this.props.activeLanguageCode}
                            availableLanguageTags={this.props.availableLanguageTags}
                            onChange={this.handleLanguageChange}
                        />
                    </div>

                    <div className="sitegen-footer__logos-and-links">
                        <Logos items={logos} />

                        <div className="sitegen-footer__ratings-wrapper">
                            <AgeRatings ratings={ratingsConfig} />
                        </div>
                    </div>

                    <div className="sitegen-footer__nav-wrapper">
                        <LegalsLinks items={legalsLinks} />
                    </div>

                    <div className="sitegen-footer__copyright-msg">
                        {copyrightText}
                    </div>
                </div>
            </div>
        );
    }
}

export default translate()(Footer);
